import { FormEvent, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { EncabezadoOrigen } from '../../Components/EncabezadoOrigen/EncabezadoOrigen';
import { conAsterisco } from '../../Components/SegUI/SegRequerido/conAsterisco';
import { SegSelect, SegSelectOption } from '../../Components/SegUI/SegSelect/SegSelect';
import { Sucursal } from '../../Services/API/bff/ventaPresencial/client';
import { useConfiguracionEmpresa } from '../../Services/Providers/useConfiguracionEmpresa';
import { DatosVendedor, MAX_NOMBRE_VENDEDOR } from '../../Services/ventaPresencial/datosVendedor';

const COLOR_TEXTO_AYUDA = '#5C5C75';

interface Props {
  /** Datos vigentes del asesor en este dispositivo (si ya cotizó en las últimas 8 horas). */
  inicial: DatosVendedor | null;
  /** Sucursales del origen, en el orden del BFF. Vacía: el origen no tiene lista y se pide solo el nombre. */
  sucursales: Sucursal[];
  onEmpezar: (datos: DatosVendedor) => void;
}

/**
 * Primera pantalla de /auto/vendedor: el asesor se identifica antes de cotizar
 * (US 23446, maqueta aprobada el 21/10). Celular, tablet y PC.
 */
export const PantallaVendedor = ({ inicial, sucursales, onEmpezar }: Props) => {
  const methods = useForm(); // SegSelect necesita un FormProvider alrededor.
  const { config, origen } = useConfiguracionEmpresa();
  const primario = config?.colors?.primary || '#2C7DFD';

  const [nombre, setNombre] = useState(inicial?.nombre ?? '');
  const conSucursales = sucursales.length > 0;
  // El asesor ve el nombre; se guarda el código, que es lo que se registra en el BFF.
  const opciones: SegSelectOption[] = useMemo(() => sucursales.map((s) => ({ value: s.codigo, label: s.nombre })), [sucursales]);
  // Si la sucursal guardada ya no está en la lista (la cambiaron en el BFF), se conserva el nombre y se vuelve a elegir.
  const [sucursal, setSucursal] = useState<SegSelectOption | null>(
    inicial ? opciones.find((o) => o.value === inicial.sucursal) ?? null : null
  );
  const completo = nombre.trim() !== '' && (!conSucursales || sucursal !== null);
  const datosPedidos = conSucursales ? 'tu nombre y tu sucursal' : 'tu nombre';

  const empezar = (evento: FormEvent) => {
    evento.preventDefault();
    if (!completo) return;
    onEmpezar({ nombre: nombre.trim(), sucursal: conSucursales && sucursal ? sucursal.value : '' });
  };

  return (
    <div style={{ color: '#1B1B2F' }}>
      {/* Sin alto ni fondo propios: usa los de la página, así el pie queda abajo y sin cortes, como en el cotizador. */}
      {/* El mismo encabezado que el cotizador, para que la landing y el cotizador se vean iguales. */}
      <EncabezadoOrigen origen={origen} config={config} />
      <main className="container" style={{ maxWidth: 560, padding: '32px 16px' }}>
        <FormProvider {...methods}>
          <form
            onSubmit={empezar}
            style={{
              background: '#FFFFFF',
              borderRadius: 16,
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 24,
              boxShadow: '0 2px 12px rgba(5,0,195,0.08)',
            }}
          >
            <div>
              <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: primario }}>
                Nueva cotización de seguro de auto
              </h1>
              {/* Corto y directo para el asesor (US 23866); el mismo con datos precargados. */}
              <p style={{ margin: '8px 0 0', fontSize: 16, color: '#4A4A63' }}>
                Completá {datosPedidos}.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label htmlFor="vendedor-nombre" style={{ fontWeight: 600 }}>
                {conAsterisco('Tu nombre')}
              </label>
              <input
                id="vendedor-nombre"
                type="text"
                className="form-control"
                maxLength={MAX_NOMBRE_VENDEDOR}
                autoCapitalize="words"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder="Ej.: María López"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                style={{ height: 52, fontSize: 16 }}
              />
              <span style={{ fontSize: 13, color: COLOR_TEXTO_AYUDA, textAlign: 'right' }}>
                {nombre.length}/{MAX_NOMBRE_VENDEDOR}
              </span>
            </div>

            {/* Sin lista cargada para el origen no hay qué elegir: se cotiza igual y el lead queda sin sucursal. */}
            {conSucursales && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span id="vendedor-sucursal-etiqueta" style={{ fontWeight: 600 }}>
                  {conAsterisco('Sucursal')}
                </span>
                <SegSelect
                  id="vendedor-sucursal"
                  name="sucursal"
                  label=""
                  options={opciones}
                  selected={sucursal}
                  setSelected={setSucursal}
                  placeholder="Escribí o seleccioná el nombre de tu sucursal"
                  width="100%"
                  height="lg"
                  openDefault={false}
                  buscable
                  ariaLabelledBy="vendedor-sucursal-etiqueta"
                  // Pantalla corta: llevar el campo arriba de todo escondía el título y el nombre.
                  subirSoloLoJusto
                  hoverBorderColor={primario}
                  focusBorderColor={primario}
                />
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                type="submit"
                disabled={!completo}
                aria-describedby={completo ? undefined : 'vendedor-ayuda-boton'}
                style={{
                  height: 54,
                  border: 'none',
                  borderRadius: 28,
                  fontSize: 17,
                  fontWeight: 700,
                  background: completo ? primario : '#C9C9DA',
                  color: completo ? '#FFFFFF' : '#3D3D55',
                }}
              >
                Empezar a cotizar
              </button>
              {!completo && (
                <span id="vendedor-ayuda-boton" style={{ fontSize: 13, color: COLOR_TEXTO_AYUDA, textAlign: 'center' }}>
                  Completá {datosPedidos} para seguir
                </span>
              )}
            </div>
          </form>
        </FormProvider>
      </main>
    </div>
  );
};
